import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';
import { calculateAndFormatChange, calculateChange, getIndicatorClass } from 'webapp/utils/number';

import joinClasses from 'webapp/helpers/join-classes';
import didCacheUpdate from 'webapp/modifiers/did-cache-update';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BlockContentCommonValueComponent from 'webapp/components/block/content/common/value';
import BlockDebugButtonComponent from 'webapp/components/block/debug-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import ForecastsMyChartComponent from 'webapp/components/forecasts/my/chart';
import LoaderSvgComponent from 'webapp/components/loader-svg';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Forecast from 'webapp/models-custom/forecast';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import DChartComparePoint from 'webapp/d-chart/point/compare-point';

interface Args {
	model: Forecast;
	hideSort: boolean;
	openDetails: (model: Forecast) => void;
	recalculate: (model: Forecast) => void;
	delete: (model: Forecast) => void;
	skipVisibilityCheck?: boolean;
}

export type Action = 'openDetails' | 'recalculate' | 'delete';
export type Option = PopupListItem<Action>;

export default class ForecastsMyGridItemComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;

	@tracked isVisibleOnScreen: boolean;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.isVisibleOnScreen = !!args.skipVisibilityCheck;
	}

	@tracked contextOpened = false;

	get options(): Option[] {
		const options: Option[] = [];

		options.push({
			icon: 'EyeOpen',
			text: 'View',
			action: 'openDetails',
		});

		if (this.args.model.canCurrentUserEdit) {
			options.push({
				icon: 'Refresh',
				text: 'Recalculate',
				action: 'recalculate',
			});

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		return options;
	}

	get forecastTitle() {
		return this.args.model.title;
	}

	get visualization() {
		return this.args.model.block?.blockSetup.visualization as VisualizationLineChart;
	}

	get series() {
		return this.visualization?.series || [];
	}

	@cached
	get forecastPoint() {
		const series = this.series.find((s) => s.isForecast);
		if (series) {
			return series.getLastPointWithValue();
		}
		return;
	}

	@cached
	get currentSeries() {
		return this.series.find((s) => s.isMain);
	}

	@cached
	get currentPointForCompare() {
		const series = this.currentSeries;
		if (series) {
			const forecastPoint = this.forecastPoint,
				currentPoint = series.getLastPointWithValue();
			if (currentPoint && forecastPoint && forecastPoint.index < currentPoint.index) {
				// take point at the same index as forecast point
				const comparePoint = series.points.find((p) => p.index === forecastPoint.index);
				if (comparePoint) {
					return comparePoint;
				}
			}
			return currentPoint;
		}
		return;
	}

	@cached
	get comparePoint() {
		const forecastPoint = this.forecastPoint,
			point = this.currentPointForCompare;
		if (forecastPoint && point?.hasValue) {
			const metric = this.args.model.metricSetup?.metric;
			if (!metric) {
				return;
			}
			const changeFormat = metric?.defaults.changeFormat,
				changeFunction = metric?.defaults.changeFunction;

			const change = calculateChange(point.value, forecastPoint.value, changeFunction)!;

			const changeStr = calculateAndFormatChange(
				point.value,
				forecastPoint.value,
				changeFormat,
				changeFunction,
			);

			return new DChartComparePoint({
				primaryPoint: forecastPoint,
				value: point.value,
				valueStr: point.valueStr,
				change,
				changeStr,
				indicatorClass: getIndicatorClass(change, !!metric.changeInvert),
			});
		}
		return;
	}

	@cached
	get tooltipText() {
		if (this.hasForecastError) {
			return this.forecastErrorMessage || '';
		}

		if (this.hasRegressorError) {
			return this.regressorErrorMessage || '';
		}

		let text = '';
		const model = this.args.model;

		const seasonalityText = model.seasonalityTexts.join(', ');
		if (seasonalityText) {
			text += `Seasonality: <b>${seasonalityText}</b><br>`;
		}

		const holidayText = model.holidayText;
		if (holidayText) {
			text += `Include holidays: <b>${holidayText}</b><br>`;
		}

		const lastSyncText = model.lastSyncText;
		if (lastSyncText) {
			text += `<p class="fade">Last updated: ${lastSyncText}</p>`;
		}

		return text;
	}

	get tooltipIconName() {
		return this.hasAnyError ? 'AlertFill' : 'InfoFill';
	}

	get annotationContext() {
		return { type: 'forecast', id: this.args.model.id! };
	}

	get hasForecastError(): boolean {
		return this.args.model.hasStatusError;
	}

	get forecastErrorMessage(): string | undefined {
		return this.args.model.statusObjectMessage;
	}

	get hasRegressorError(): boolean {
		return (this.args.model.regressors || []).some((regressor) => regressor.hasStatusError);
	}

	get regressorErrorMessage(): string | undefined {
		const errorRegressor = (this.args.model.regressors || []).find(
			(regressor) => regressor.hasStatusError,
		);
		return errorRegressor?.statusObjectMessage;
	}

	get hasAnyError(): boolean {
		return this.hasForecastError || this.hasRegressorError;
	}

	@action
	openContextDropdown(e: PointerEvent): void {
		if (this.contextOpened) {
			return;
		}

		const attachTo = e.target as HTMLElement;
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;
				}
			});
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openDetails() {
		this.args.openDetails(this.args.model);
	}

	@action
	recalculate() {
		this.args.recalculate(this.args.model);
	}

	@action
	delete(): void {
		this.args.delete(this.args.model);
	}

	@action
	updateDataCache() {
		this.visualization?.checkToUpdateDataCache();
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					@model.animationClass
					"card"
					"card--visualization"
					"flex-item"
					"filter-sort-item"
					(if this.contextOpened "contextOpened")
					(if @hideSort "hideSort")
				)
			}}
			{{didCacheUpdate this.updateDataCache @model.sample.sampledata}}
		>
			{{#if this.isVisibleOnScreen}}

				<div class="drag" data-id={{@model.id}}>
					<CommonUiIconComponent @name="ArrowsMove" @size="md" />
				</div>

				<div class="top">
					<div class="top__left">
					</div>
					<div class="top__right">
						<div role="button" class="context" {{on "click" this.openContextDropdown}}>
							<CommonUiIconComponent @name="Ellipsis" @size="md" />
						</div>
					</div>
				</div>

				<div class="info">
					<div class="title-with-tooltip">
						<EditableModelPropertyComponent @model={{@model}} @canEdit={{false}} />
						{{#if this.tooltipText}}
							<ToolTipWithIconComponent
								@text={{this.tooltipText}}
								@maxWidth={{230}}
								@iconName={{this.tooltipIconName}}
							/>
						{{/if}}
					</div>
					{{#if @model.sample}}
						{{#if this.forecastPoint}}
							<BlockContentCommonValueComponent
								@model={{this.forecastPoint}}
								@loading={{this.forecastPoint.loading}}
								@skipClickable={{true}}
							/>
						{{/if}}
					{{else}}
						<span class="bc__value">
							<LoaderSvgComponent />
						</span>
					{{/if}}
				</div>
				{{#if this.hasForecastError}}
					<div class="visualization visualization--error">
						<img alt="Forecast" src="/images/forecast_graph_error.png" />
					</div>
				{{else}}
					<div class="visualization">
						{{#if this.visualization.series}}
							<div class="preview preview-chart colorTheme-1">
								<ForecastsMyChartComponent
									@visualization={{this.visualization}}
									@height={{85}}
									@width="100%"
									@annotationContext={{this.annotationContext}}
									@canAnnotate={{false}}
								/>
							</div>
						{{/if}}
					</div>

					<div class="compare-action-area">
						{{#if this.comparePoint}}
							<div class="compares">
								<div
									class="bc__compare bc__compare--{{this.comparePoint.indicatorClass}}"
								>
									<span
										class="bc__compare__change"
									>{{this.comparePoint.changeStr}}
									</span>
									<small class="bc__compare__text">
										vs current value ({{this.comparePoint.valueStr}})
									</small>
								</div>
							</div>
						{{/if}}

						<div class="compare-action-area__actions">
							<SubmitButtonComponent
								@text="View"
								@onTrigger={{this.openDetails}}
								@noDelay={{true}}
							/>
						</div>
					</div>
				{{/if}}

				<div class="bottom">

					<div class="source">
						<img
							src={{@model.source.realAvatar}}
							width="16"
							height="16"
							alt={{@model.source.sourceType.name}}
						/>
						<span>
							{{@model.source.title}}
							<ToolTipComponent @text={{@model.source.title}} />
						</span>
					</div>

					<TagContainerComponent @model={{@model}} @canEdit={{@model.isMine}} />
				</div>

				<BlockDebugButtonComponent @blockId={{@model.blockId}} />

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}
		</li>
	</template>
}
